/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import "../../../geometry.js";
import e from "../../../core/Error.js";
import { deg2rad as i, floatEqualAbsolute as t, floatEqualRelative as s } from "../../../core/mathUtils.js";
import { getMetersPerUnitForSR as r } from "../../../core/unitUtils.js";
import n from "../../../geometry/Extent.js";
import { canProjectToWGS84ComparableLonLat as l } from "../../../geometry/projection.js";
import o from "../../../geometry/SpatialReference.js";
import { create as a } from "../../../geometry/support/aaBoundingRect.js";
import { isMars as h, isMoon as c } from "../../../geometry/support/spatialReferenceUtils.js";
import { x2lon as m, y2lat as u } from "../../../geometry/support/webMercatorUtils.js";
import p from "../../../layers/support/LOD.js";
import g from "../../../layers/support/TileInfo.js";
import f from "../../../geometry/Point.js";
const x = 12;
class v {
  constructor(e) {
    const i = v.checkUnsupported(e);
    if (null != i) throw i;
    const t = e;
    this.spatialReference = t.spatialReference, this._isWebMercator = this.spatialReference.isWebMercator, this._isGCS = l(this.spatialReference) || h(this.spatialReference) || c(this.spatialReference), this.origin = [t.origin.x, t.origin.y], this.pixelSize = t.size[0], this.dpi = t.dpi;
    const s = t.lods.reduce((e, i, t) => (i.level < e.min && (e.min = i.level, e.minIndex = t), e.max = Math.max(e.max, i.level), e), {
        min: 1 / 0,
        minIndex: 0,
        max: -1 / 0
      }),
      r = t.lods[s.minIndex],
      n = 2 ** r.level;
    let o = r.resolution * n,
      a = r.scale * n;
    this.levels = new Array(s.max + 1);
    for (let l = 0; l < this.levels.length; l++) this.levels[l] = {
      resolution: o,
      scale: a,
      tileSize: [o * t.size[0], o * t.size[1]]
    }, o /= 2, a /= 2;
  }
  clone() {
    return new v(this.toTileInfo());
  }
  toTileInfo() {
    return new g({
      dpi: this.dpi,
      origin: new f({
        x: this.origin[0],
        y: this.origin[1],
        spatialReference: this.spatialReference
      }),
      size: [this.pixelSize, this.pixelSize],
      spatialReference: this.spatialReference,
      lods: this.levels.map((e, i) => new p({
        level: i,
        scale: e.scale,
        resolution: e.resolution
      }))
    });
  }
  getExtent(e, i, t, s) {
    const r = this.levels[e],
      n = r.tileSize[0],
      l = r.tileSize[1];
    s[0] = this.origin[0] + t * n, s[2] = this.origin[0] + (t + 1) * n, s[3] = this.origin[1] - i * l, s[1] = this.origin[1] - (i + 1) * l;
  }
  convertExtentToRadians(e, t) {
    this._isWebMercator ? (t[0] = m(e[0]), t[1] = u(e[1]), t[2] = m(e[2]), t[3] = u(e[3])) : this._isGCS && (t[0] = i(e[0]), t[1] = i(e[1]), t[2] = i(e[2]), t[3] = i(e[3]));
  }
  getExtentGeometry(e, i, t, s = new n()) {
    return this.getExtent(e, i, t, M), s.spatialReference = this.spatialReference, s.xmin = M[0], s.ymin = M[1], s.xmax = M[2], s.ymax = M[3], s.zmin = void 0, s.zmax = void 0, s;
  }
  ensureMaxLod(e) {
    if (null == e) return !1;
    let i = !1;
    for (; this.levels.length <= e;) {
      const e = this.levels[this.levels.length - 1],
        t = e.resolution / 2;
      this.levels.push({
        resolution: t,
        scale: e.scale / 2,
        tileSize: [t * this.pixelSize, t * this.pixelSize]
      }), i = !0;
    }
    return i;
  }
  capMaxLod(e) {
    this.levels.length > e + 1 && (this.levels.length = e + 1);
  }
  getMaxLod() {
    return this.levels.length - 1;
  }
  scaleAtLevel(e) {
    return this.levels[0].scale / 2 ** e;
  }
  levelAtScale(e) {
    const i = this.levels[0].scale;
    return e >= i ? 0 : Math.log(i / e) * Math.LOG2E;
  }
  resolutionAtLevel(e) {
    return this.levels[0].resolution / 2 ** e;
  }
  compatibleWith(e) {
    if (!(e instanceof v)) {
      if (v.checkUnsupported(e)) return !1;
      e = new v(e);
    }
    if (!e.spatialReference.equals(this.spatialReference)) return !1;
    if (e.pixelSize !== this.pixelSize) return !1;
    const i = Math.min(this.levels.length, e.levels.length) - 1,
      s = this.levels[i].resolution;
    let r = .5 * s;
    if (!t(e.origin[0], this.origin[0], r) || !t(e.origin[1], this.origin[1], r)) return !1;
    return r = .5 * s / 2 ** i / this.pixelSize * x, t(s, e.levels[i].resolution, r);
  }
  rootTilesInExtent(e, i = null, t = 1 / 0) {
    const s = new Array(),
      r = this.levels[0].tileSize;
    if (null == e || 0 === r[0] || 0 === r[1]) return s;
    v.computeRowColExtent(e, r, this.origin, M);
    let n = M[1],
      l = M[3],
      o = M[0],
      a = M[2];
    const h = a - o,
      c = l - n;
    if (h * c > t) {
      const e = Math.floor(Math.sqrt(t));
      c > e && (n = n + Math.floor(.5 * c) - Math.floor(.5 * e), l = n + e), h > e && (o = o + Math.floor(.5 * h) - Math.floor(.5 * e), a = o + e);
    }
    for (let m = n; m < l; m++) for (let e = o; e < a; e++) s.push([0, m, e]);
    return null != i && (i[0] = this.origin[0] + o * r[0], i[1] = this.origin[1] - l * r[1], i[2] = this.origin[0] + a * r[0], i[3] = this.origin[1] - n * r[1]), s;
  }
  static computeRowColExtent(e, i, t, s) {
    const r = .001 * (e[2] - e[0] + (e[3] - e[1]));
    s[0] = Math.max(0, Math.floor((e[0] + r - t[0]) / i[0])), s[2] = Math.max(0, Math.ceil((e[2] - r - t[0]) / i[0])), s[1] = Math.max(0, Math.floor((t[1] - e[3] + r) / i[1])), s[3] = Math.max(0, Math.ceil((t[1] - e[1] - r) / i[1]));
  }
  static isPowerOfTwo(e) {
    const i = e.lods,
      t = i[0].resolution * 2 ** i[0].level;
    return !i.some(e => !s(e.resolution, t / 2 ** e.level));
  }
  static hasGapInLevels(e) {
    const i = e.lods.map(e => e.level);
    i.sort((e, i) => e - i);
    for (let t = 1; t < i.length; t++) if (i[t] !== i[0] + t) return !0;
    return !1;
  }
  static tileSizeSupported(e) {
    const i = e.size[1];
    return i === e.size[0] && 0 == (i & i - 1) && i >= 128 && i <= 512;
  }
  static hasOriginPerLODs(e) {
    const i = e.origin;
    return e.lods.some(e => null != e.origin && (e.origin[0] !== i.x || e.origin[1] !== i.y));
  }
  static getMissingTileInfoError() {
    return new e("tilingscheme:tile-info-missing", "Tiling scheme must have tiling information");
  }
  static checkUnsupported(i) {
    return null == i ? w() : i.lods.length < 1 ? new e("tilingscheme:generic", "Tiling scheme must have at least one level") : v.isPowerOfTwo(i) ? v.hasGapInLevels(i) ? new e("tilingscheme:gaps", "Tiling scheme levels must not have gaps between min and max level") : v.tileSizeSupported(i) ? v.hasOriginPerLODs(i) ? new e("tilingscheme:multiple-origin", "Tiling scheme levels must not have their own origin") : null : new e("tilingscheme:tile-size", "Tiles must be square and size must be one of [128, 256, 512]") : new e("tilingscheme:power-of-two", "Tiling scheme must be power of two");
  }
  static fromExtent(e, i) {
    const t = e[2] - e[0],
      s = e[3] - e[1],
      n = r(i),
      l = 1.2 * Math.max(t, s),
      o = 256,
      a = 96,
      h = .0254,
      c = new v(new g({
        size: [o, o],
        origin: new f({
          x: e[0] - .5 * (l - t),
          y: e[3] + .5 * (l - s)
        }),
        lods: [new p({
          level: 0,
          resolution: l / o,
          scale: 1 / (o / a * h / (l * n))
        })],
        spatialReference: i
      }));
    return c.ensureMaxLod(20), c;
  }
  static makeWebMercatorAuxiliarySphere(e) {
    const i = new v(v.WebMercatorAuxiliarySphereTileInfo);
    return i.ensureMaxLod(e), i;
  }
  static makeGCSWithTileSize(e, i = 256, t = 16) {
    const s = 256 / i,
      r = new v(new g({
        size: [i, i],
        origin: new f({
          x: -180,
          y: 90,
          spatialReference: e
        }),
        spatialReference: e,
        lods: [new p({
          level: 0,
          resolution: .703125 * s,
          scale: 295497598.570834 * s
        })]
      }));
    return r.ensureMaxLod(t), r;
  }
  get test() {
    return {
      isWebMercator: this._isWebMercator,
      isGCS: this._isGCS
    };
  }
}
function w() {
  return new e("tilingscheme:tile-info-missing", "Tiling scheme must have tiling information");
}
v.WebMercatorAuxiliarySphereTileInfo = new g({
  size: [256, 256],
  origin: new f({
    x: -20037508.342787,
    y: 20037508.342787,
    spatialReference: o.WebMercator
  }),
  spatialReference: o.WebMercator,
  lods: [new p({
    level: 0,
    resolution: 156543.03392800014,
    scale: 591657527.591555
  })]
}), v.WebMercatorAuxiliarySphere = v.makeWebMercatorAuxiliarySphere(19);
const M = a();
export { v as TilingScheme, w as getMissingTileInfoError };